// AdminSidebar — 2-tier orchestrator (80px primary + 240px secondary).
// Clone từ attendance-app's AdminSidebar.jsx (line 1-142) parameterized.
// Khác Core: nhận `menu` qua prop thay vì import `adminMenu`. detailPatterns + customSecondary
// cũng prop để consumer override Core-specific routes.

import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { findActiveGroup } from '../../../navigation/menuUtils.js';
import SidebarPrimary from './SidebarPrimary.jsx';
import SidebarSecondary from './SidebarSecondary.jsx';

export default function AdminSidebar({
  menu = [],
  brand = {},
  homeLink = null,
  detailPatterns = [], // [RegExp] — pages ẩn secondary (ví dụ /inbox, /chat fullscreen)
  fullscreenPatterns = [], // [RegExp] — fullscreen routes cũng phải ẩn secondary (vd /tickets/:id)
  customSecondary = {},
  badgeCounts = {},
  // Slot pattern — consumer override SidebarPrimary's top/bottom zones (Core: MiniCallBubble + user menu modal).
  primaryTopSlot,
  primaryBottomSlot,
  // forceCompact: consumer-controlled extra compact toggle (vd Core /approvals?view=table khi viewport < 1720).
  forceCompact = false,
}) {
  const location = useLocation();
  const routeGroup = useMemo(() => findActiveGroup(menu, location.pathname), [menu, location.pathname]);
  const [selectedGroup, setSelectedGroup] = useState(routeGroup || menu[0]);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [hoverSecondary, setHoverSecondary] = useState(false);
  const [autoHide, setAutoHide] = useState(() =>
    typeof window !== 'undefined' && localStorage.getItem('sidebar_auto_hide') === 'true');

  // Đồng bộ khi toggle ở /me → Tiện ích (cùng tab: event tự dispatch, khác tab: storage native)
  useEffect(() => {
    const onStorage = () => setAutoHide(localStorage.getItem('sidebar_auto_hide') === 'true');
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const isDetailPage = detailPatterns.some((p) => p.test(location.pathname));
  const isFullscreen = fullscreenPatterns.some((p) => p.test(location.pathname));
  const isSingleLinkGroup = !!(selectedGroup && !selectedGroup.items?.length);
  const shouldHide = autoHide || isDetailPage || isFullscreen || isSingleLinkGroup || forceCompact;
  const allowHoverPeek = shouldHide && !isSingleLinkGroup;

  useEffect(() => {
    if (routeGroup) setSelectedGroup(routeGroup);
    setHoverSecondary(false);
  }, [routeGroup, location.pathname]);

  useEffect(() => {
    if (!selectedGroup || selectedGroup.key === routeGroup?.key) return;
    if (shouldHide && selectedGroup.items?.length > 0) setHoverSecondary(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGroup]);

  return (
    <>
      <aside className="hidden lg:flex fixed inset-y-0 left-0 z-50">
        <div
          onMouseEnter={() => allowHoverPeek && setHoverSecondary(true)}
          onMouseLeave={() => allowHoverPeek && setHoverSecondary(false)}
        >
          <SidebarPrimary
            menu={menu}
            brand={brand}
            homeLink={homeLink}
            onSelectGroup={setSelectedGroup}
            badgeCounts={badgeCounts}
            topSlot={primaryTopSlot}
            bottomSlot={primaryBottomSlot}
          />
        </div>
        {(!shouldHide || hoverSecondary) && !isSingleLinkGroup && (
          <div
            className={shouldHide ? 'fixed left-[80px] top-0 bottom-0 z-50 shadow-2xl' : ''}
            onMouseEnter={() => allowHoverPeek && setHoverSecondary(true)}
            onMouseLeave={() => allowHoverPeek && setHoverSecondary(false)}
          >
            <SidebarSecondary
              menu={menu}
              group={selectedGroup}
              badgeCounts={badgeCounts}
              customSecondary={customSecondary}
            />
          </div>
        )}
      </aside>

      <button
        className="lg:hidden fixed top-4 left-4 z-40 w-11 h-11 rounded-xl bg-slate-950 text-white flex items-center justify-center shadow-lg"
        onClick={() => setMobileOpen(true)}
      >
        <Menu size={20} />
      </button>

      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMobileOpen(false)} />
          <div className="relative flex h-full">
            <SidebarPrimary
              menu={menu}
              brand={brand}
              homeLink={homeLink}
              onSelectGroup={setSelectedGroup}
              onOpenMobile={() => setMobileOpen(false)}
              badgeCounts={badgeCounts}
              topSlot={primaryTopSlot}
              bottomSlot={primaryBottomSlot}
            />
            {!isSingleLinkGroup && (
              <div className="relative">
                <button
                  onClick={() => setMobileOpen(false)}
                  className="absolute top-4 right-4 z-10 p-2 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100"
                >
                  <X size={18} />
                </button>
                <SidebarSecondary
                  menu={menu}
                  group={selectedGroup}
                  badgeCounts={badgeCounts}
                  customSecondary={customSecondary}
                  mobile
                  onNavigate={() => setMobileOpen(false)}
                />
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
